import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Space, Typography, Select, Switch, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const columnTypes = [
  { label: 'Text', value: 'text' },
  { label: 'Text Area', value: 'textarea' },
  { label: 'Number', value: 'number' },
  { label: 'Currency', value: 'currency' },
  { label: 'Percentage', value: 'percentage' },
  { label: 'Date', value: 'date' },
  { label: 'Date & Time', value: 'datetime' },
  { label: 'Time', value: 'time' },
  { label: 'Date Range', value: 'daterange' },
  { label: 'Dropdown', value: 'select' },
  { label: 'Multi-Select', value: 'multiselect' },
  { label: 'Radio Buttons', value: 'radio' },
  { label: 'Checkboxes', value: 'checkbox' },
  { label: 'Formula', value: 'formula' },
];

export default function TableConfigPanel({ open, onClose, onSave, initialConfig }) {
  // Deep clone columns to avoid mutating readonly props
  const [columns, setColumns] = useState(
    initialConfig?.columns ? initialConfig.columns.map(col => ({ ...col, options: col.options ? [...col.options] : [] })) : []
  );
  const [allowAdd, setAllowAdd] = useState(initialConfig?.allowAdd !== false);
  const [allowDelete, setAllowDelete] = useState(initialConfig?.allowDelete !== false);
  const [editMode, setEditMode] = useState(initialConfig?.editMode || 'inline'); // 'inline' or 'row'
  const [orientation, setOrientation] = useState(initialConfig?.orientation || 'columns'); // 'columns' or 'rows'

  // Reset state when modal opens with new config
  useEffect(() => {
    if (open && initialConfig) {
      setColumns(
        initialConfig.columns ? initialConfig.columns.map(col => ({ ...col, options: col.options ? [...col.options] : [] })) : []
      );
      setAllowAdd(initialConfig.allowAdd !== false);
      setAllowDelete(initialConfig.allowDelete !== false);
      setEditMode(initialConfig.editMode || 'inline');
      setOrientation(initialConfig.orientation || 'columns');
    }
  }, [open, initialConfig]);

  const handleAddColumn = () => {
    setColumns([
      ...columns,
      {
        key: `col_${Date.now()}`,
        title: orientation === 'columns' ? 'New Column' : 'New Row',
        type: 'text',
        width: 150,
        required: false,
        formula: '',
      },
    ]);
  };

  const handleRemoveColumn = (index) => {
    setColumns(columns.filter((_, i) => i !== index));
  };

  const handleColumnChange = (index, key, value) => {
    const newColumns = [...columns];
    newColumns[index] = { ...newColumns[index], [key]: value };
    setColumns(newColumns);
  };

  const handleSave = () => {
    const config = {
      columns,
      allowAdd,
      allowDelete,
      editMode,
      orientation,
      minRows: 1,
      maxRows: 100,
    };

    onSave(config);
    onClose();
  };

  return (
    <Modal
      title="Table/Grid Configuration"
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      width={900}
      okText="Save"
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>Table Orientation</Text>
          <Select
            style={{ width: '100%' }}
            value={orientation}
            onChange={setOrientation}
            options={[
              {
                label: 'Column-based (traditional spreadsheet with column headers)',
                value: 'columns'
              },
              {
                label: 'Row-based (row headers on left, values on right)',
                value: 'rows'
              },
            ]}
          />
          <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginTop: '4px' }}>
            {orientation === 'columns'
              ? 'Users will add rows of data under column headers'
              : 'Users will fill in values for each predefined row'}
          </Text>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        <div>
          <Text strong>{orientation === 'columns' ? 'Column' : 'Row'} Definitions</Text>
          <Text type="secondary" style={{ display: 'block', fontSize: '13px', marginTop: '4px', marginBottom: '12px' }}>
            {orientation === 'columns'
              ? 'Define the columns for your grid. Use formulas for calculated fields.'
              : 'Define the rows for your form. Each row will have a label and input field.'}
          </Text>
        </div>

        {columns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">No {orientation === 'columns' ? 'columns' : 'rows'} defined</Text>
          </div>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {columns.map((column, index) => (
              <div
                key={index}
                style={{
                  padding: '16px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  background: '#F9FAFB',
                }}
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Space style={{ width: '100%' }}>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        {orientation === 'columns' ? 'Column Title' : 'Row Header'}
                      </Text>
                      <Input
                        placeholder={orientation === 'columns' ? 'e.g., Item Name' : 'e.g., Full Name'}
                        value={column.title}
                        onChange={(e) => handleColumnChange(index, 'title', e.target.value)}
                      />
                    </div>

                    <div style={{ width: '140px' }}>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Type
                      </Text>
                      <Select
                        style={{ width: '100%' }}
                        value={column.type}
                        onChange={(value) => handleColumnChange(index, 'type', value)}
                        options={columnTypes}
                      />
                    </div>

                    <div style={{ width: '100px' }}>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Width
                      </Text>
                      <Input
                        type="number"
                        placeholder="150"
                        value={column.width}
                        onChange={(e) => handleColumnChange(index, 'width', parseInt(e.target.value))}
                      />
                    </div>

                    <div style={{ marginTop: '18px' }}>
                      <Switch
                        checked={column.required}
                        onChange={(checked) => handleColumnChange(index, 'required', checked)}
                      />
                      <Text style={{ marginLeft: '8px', fontSize: '12px' }}>Required</Text>
                    </div>

                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => handleRemoveColumn(index)}
                      style={{ marginTop: '18px' }}
                    />
                  </Space>

                  {column.type === 'formula' && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Formula (Excel syntax: e.g., =A1+B1)
                      </Text>
                      <TextArea
                        rows={2}
                        placeholder="e.g., =SUM(B2:B10) or =C2*D2"
                        value={column.formula}
                        onChange={(e) => handleColumnChange(index, 'formula', e.target.value)}
                      />
                    </div>
                  )}

                  {['select', 'multiselect', 'radio', 'checkbox'].includes(column.type) && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Options (one per line)
                      </Text>
                      <TextArea
                        rows={4}
                        placeholder={'Option 1\nOption 2\nOption 3'}
                        value={(column.options || []).join('\n')}
                        onChange={(e) => {
                          // Split by newlines but keep empty lines during editing
                          const options = e.target.value.split('\n');
                          handleColumnChange(index, 'options', options);
                        }}
                        onBlur={(e) => {
                          // Only filter out truly empty options when user leaves the field
                          const cleanOptions = e.target.value.split('\n').filter(opt => opt.trim()).filter(opt => opt);
                          if (cleanOptions.length > 0) {
                            handleColumnChange(index, 'options', cleanOptions);
                          }
                        }}
                      />
                    </div>
                  )}
                </Space>
              </div>
            ))}
          </Space>
        )}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={handleAddColumn}
          block
          style={{ marginTop: '12px' }}
        >
          Add {orientation === 'columns' ? 'Column' : 'Row'}
        </Button>

        <Divider style={{ margin: '16px 0' }} />

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {orientation === 'columns' && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>Table Behavior</Text>
              <Space direction="vertical" size="small">
                <div>
                  <Switch checked={allowAdd} onChange={setAllowAdd} />
                  <Text style={{ marginLeft: '8px' }}>Allow users to add rows</Text>
                </div>
                <div>
                  <Switch checked={allowDelete} onChange={setAllowDelete} />
                  <Text style={{ marginLeft: '8px' }}>Allow users to delete rows</Text>
                </div>
              </Space>
            </div>
          )}

          {orientation === 'columns' && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>Edit Mode</Text>
              <Select
                style={{ width: '100%' }}
                value={editMode}
                onChange={setEditMode}
                options={[
                  { label: 'Inline Edit (Excel-like, always editable)', value: 'inline' },
                  { label: 'Row Edit (Edit/Save buttons per row)', value: 'row' },
                ]}
              />
            </div>
          )}
        </Space>

        <div style={{ background: '#F9F5FF', padding: '12px', borderRadius: '8px', marginTop: '16px' }}>
          <Text strong style={{ fontSize: '13px' }}>
            Configuration Preview:
          </Text>
          <pre style={{ marginTop: '8px', fontSize: '12px', background: '#fff', padding: '8px', borderRadius: '4px', overflow: 'auto', maxHeight: '150px' }}>
            {JSON.stringify({ orientation, columns, allowAdd, allowDelete, editMode }, null, 2)}
          </pre>
        </div>
      </Space>
    </Modal>
  );
}
